import { inject, Pipe, PipeTransform } from '@angular/core';
import { SmartContactsApiService } from '@shared/modules/contacts/shell/contacts-api-cache/smart-contacts-api.service';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { CommentChatModel } from '@dta/shared/models-api-loop/comment/comment.model';
import { SharedTagReactionModel } from '@dta/shared/models-api-loop/shared-tag/shared-tag.model';
import { combineLatest, Observable, take } from 'rxjs';
import { map } from 'rxjs/operators';

interface ReactionTagData {
  reaction: string;
  reactionCount: number;
  isMyReaction: boolean;
  reactionUserNames: string[];
}

@Pipe({
  name: 'getReactionTags$',
  standalone: true
})
export class GetReactionTagsPipe implements PipeTransform {
  private readonly userAuthService: UserAuthService = inject(UserAuthService);
  private readonly smartContactsApiService: SmartContactsApiService = inject(SmartContactsApiService);

  transform(comment: CommentChatModel): Observable<ReactionTagData[]> {
    const reactionTags =
      (comment.sharedTags?.tags?.resources?.filter(
        tag => tag.$type === SharedTagReactionModel.type
      ) as SharedTagReactionModel[]) ?? [];

    const userIds = reactionTags.map(reactionTag => reactionTag.userId);

    return combineLatest([
      this.userAuthService.currentSession$.pipe(
        take(1),
        map(current => current?.user?.id)
      ),
      this.smartContactsApiService.getByIds$({
        contactIds: userIds.map(userId => ({ contactId: userId, authorizedId: comment?.parent?.id }))
      })
    ]).pipe(
      map(([currentUserId, users]) => {
        const data = reactionTags.reduce((acc, reaction) => {
          const reactionUser = users.find(user => user.id === reaction.userId);

          acc[reaction.name] = {
            reaction: reaction.name,
            reactionCount: (acc[reaction.name]?.reactionCount ?? 0) + 1,
            isMyReaction: acc[reaction.name]?.isMyReaction || reaction.userId === currentUserId,
            reactionUserNames: [
              ...(acc[reaction.name]?.reactionUserNames ?? []),
              reaction.userId === currentUserId ? 'You' : reactionUser?.name
            ]
          };
          return acc;
        }, {});

        return Object.values(data);
      })
    );
  }
}
